import React, { useState } from 'react';
import {
    Phone,
    Mail,
    Ticket,
    Bot,
    BarChart2,
    ShieldCheck,
    Zap,
    Clock,
    Lock,
    ChevronRight,
    LayoutGrid,
    Share2,
    CheckCircle2,
    Users,
    MessageSquare,
    Mic
} from 'lucide-react';
import PublicPageLayout from '../../layouts/PublicPageLayout';

export default function FeaturesPage() {
    const [activeTab, setActiveTab] = useState('all');

    const tabs = [
        { id: 'all', label: 'Tous les outils', icon: LayoutGrid },
        { id: 'telephony', label: 'Téléphonie', icon: Phone },
        { id: 'messaging', label: 'Messagerie', icon: Mail },
        { id: 'tickets', label: 'Gestion des tickets', icon: Ticket },
        { id: 'ai', label: 'IA & Automatisation', icon: Bot },
        { id: 'analytics', label: 'Analyse & Rapports', icon: BarChart2 },
        { id: 'integrations', label: 'Intégrations', icon: Share2 },
        { id: 'security', label: 'Sécurité', icon: ShieldCheck },
    ];

    const featureCards = [
        {
            id: 1,
            title: 'Téléphonie Cloud',
            desc: 'Appels entrants et sortants en haute qualité avec numéros locaux et internationaux.',
            icon: Phone,
            iconColor: 'text-blue-600',
            bgColor: 'bg-blue-50',
        },
        {
            id: 2,
            title: 'Gestion des Tickets',
            desc: 'Créez, assignez et suivez les tickets jusqu’à leur résolution.',
            icon: Ticket,
            iconColor: 'text-blue-600',
            bgColor: 'bg-blue-50',
        },
        {
            id: 3,
            title: 'WhatsApp Business',
            desc: 'Gérez toutes vos conversations WhatsApp depuis une seule plateforme.',
            icon: MessageSquare,
            iconColor: 'text-emerald-600',
            bgColor: 'bg-emerald-50',
        },
        {
            id: 4,
            title: 'IA Gaston',
            desc: 'L’IA qui assiste vos agents, suggère des réponses et automatise vos tâches.',
            icon: Bot,
            iconColor: 'text-amber-500',
            bgColor: 'bg-amber-50',
        },
        {
            id: 5,
            title: 'Email & SMS',
            desc: 'Envoyez et recevez des emails et SMS instantanément.',
            icon: Mail,
            iconColor: 'text-purple-600',
            bgColor: 'bg-purple-50',
        },
        {
            id: 6,
            title: 'Rapports & Analyses',
            desc: 'Tableaux de bord avancés et rapports personnalisés en temps réel.',
            icon: BarChart2,
            iconColor: 'text-blue-600',
            bgColor: 'bg-blue-50',
        },
        {
            id: 7,
            title: 'CRM Intégré',
            desc: 'Centralisez les informations clients et suivez toutes les interactions.',
            icon: Users,
            iconColor: 'text-blue-600',
            bgColor: 'bg-blue-50',
        },
        {
            id: 8,
            title: 'Enregistrements & Historique',
            desc: 'Enregistrez vos appels et accédez à tout l’historique en quelques clics.',
            icon: Mic,
            iconColor: 'text-blue-600',
            bgColor: 'bg-blue-50',
        },
    ];

    return (
        <PublicPageLayout>

            {/* ================= HERO SECTION (FOND AJUSTÉ À L'IMAGE HERO.PNG) ================= */}
            <section className="relative w-full bg-[#FBFDFF] lg:pb-2 overflow-hidden">

                {/* Ellipse/Vague douce en arrière-plan */}
                <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-[#E2ECFD] rounded-full blur-3xl opacity-50 pointer-events-none"></div>

                <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">

                        {/* Texte Hero */}
                        <div className="lg:col-span-6 space-y-4">
                            <span className="text-xs font-extrabold text-blue-600 tracking-wider uppercase">
                                FONCTIONNALITÉS
                            </span>

                            <h1 className="text-3xl lg:text-[42px] font-extrabold text-slate-900 leading-[1.15] tracking-tight">
                                Toutes les fonctionnalités <br />
                                pour une expérience client <br />
                                <span className="text-blue-600">
                                    exceptionnelle<span className="text-amber-400">.</span>
                                </span>
                            </h1>

                            <p className="text-slate-600 text-sm leading-relaxed max-w-lg">
                                IZI Call Center regroupe tous les outils essentiels pour gérer vos communications et offrir un service client rapide, efficace et personnalisé.
                            </p>
                        </div>

                        {/* Image Hero (Casque + canaux / hero.png) */}
                        <div className="lg:col-span-6 flex justify-center items-center relative">
                            <img
                                src="/features/hero.png"
                                alt="Fonctionnalités IZI Call Center"
                                className="w-full h-auto max-w-lg object-contain mix-blend-multiply"
                            />
                        </div>

                    </div>
                </div>

                <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-[#E2ECFD] rounded-full blur-3xl opacity-50 pointer-events-none"></div>

            </section>

            {/* ================= BARRE DE NAVIGATION TAB ================= */}
            <div className="max-w-7xl mx-auto px-6 lg:px-12 mb-8 relative z-20">
                <div className="bg-white rounded-2xl p-2 border border-slate-200/80 shadow-sm flex items-center gap-1 overflow-x-auto scrollbar-none">
                    {tabs.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${isActive
                                    ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                                    }`}
                            >
                                <Icon size={15} />
                                <span>{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ================= SECTION PRINCIPALE : CARTES + IMAGE DASHBOARD ================= */}
            <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

                    {/* 8 CARTES DE FONCTIONNALITÉS (DISPOSITION HORIZONTALE) */}
                    <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {featureCards.map((card) => {
                            const Icon = card.icon;
                            return (
                                <div
                                    key={card.id}
                                    className="bg-white rounded-2xl p-3.5 border border-slate-200/70 shadow-sm hover:shadow-md transition-all flex items-start gap-3 cursor-pointer group"
                                >
                                    {/* Icône à gauche */}
                                    <div className={`w-9 h-9 rounded-xl ${card.bgColor} ${card.iconColor} flex items-center justify-center shrink-0`}>
                                        <Icon size={18} />
                                    </div>

                                    {/* Bloc Texte au milieu */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-1">
                                            <h3 className="text-xs font-extrabold text-slate-900 truncate">{card.title}</h3>
                                            <ChevronRight size={14} className="text-slate-300 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all shrink-0" />
                                        </div>
                                        <p className="text-[11px] text-slate-500 leading-tight mt-0.5">
                                            {card.desc}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* IMAGE RÉELLE DU DASHBOARD */}
                    <div className="lg:col-span-6 flex justify-center items-center">
                        <img
                            src="/hero/dashboard-izicall_center.jpg"
                            alt="Dashboard IZI Call Center"
                            className="w-full h-full rounded-3xl shadow-xl border border-slate-200/80 object-contain bg-white"
                        />
                    </div>

                </div>
            </div>

            {/* ================= SECTION GARANTIES ================= */}
            <div className="max-w-7xl mx-auto px-6 lg:px-12 mt-12 relative z-10">
                <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">

                        <div className="flex items-center gap-3 pt-2 sm:pt-0">
                            <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                <Zap size={20} />
                            </div>
                            <div>
                                <h4 className="text-xs font-bold text-slate-900">Installation rapide</h4>
                                <p className="text-[11px] text-slate-500">En moins de 5 minutes</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 pt-4 sm:pt-0 sm:pl-6">
                            <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                <CheckCircle2 size={20} />
                            </div>
                            <div>
                                <h4 className="text-xs font-bold text-slate-900">99,9%</h4>
                                <p className="text-[11px] text-slate-500">Uptime garanti</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 pt-4 lg:pt-0 lg:pl-6">
                            <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                <Clock size={20} />
                            </div>
                            <div>
                                <h4 className="text-xs font-bold text-slate-900">24/7</h4>
                                <p className="text-[11px] text-slate-500">Support dédié</p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3 pt-4 lg:pt-0 lg:pl-6">
                            <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                <Lock size={20} />
                            </div>
                            <div>
                                <h4 className="text-xs font-bold text-slate-900">Conforme</h4>
                                <p className="text-[11px] text-slate-500">RGPD & Sécurité maximale</p>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

        </PublicPageLayout>
    );
}